/**
 * @fileoverview AR-670 — HTTP adapter over the READ-ONLY mosaic-backend
 * Google Classroom API (contract: mosaic-backend/docs/frontend-integration/).
 * The Workspace connection is PER USER (branch feat-classroom-user-connection):
 * each tutor connects their own Google account. No writes ever reach Google:
 * courses are linked (never created), rosters are matched read-only, and the
 * student reads their own stream files. Grade push (phase 4) has no backend,
 * so its port stays unavailable behind the provider's capabilities.
 *
 * Every call goes through `ensureOk`: the shared axios interceptor resolves
 * 401s instead of rejecting them. Payload/error translation: ./httpMappers.ts.
 */

import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { ClassroomAdapters } from '../ports'
import { ClassroomPortError, SchoolConnection } from '../types'
import { ensureOk, normalizeError, toConnection, toLinkResult, toLinkState } from './httpMappers'
import { httpSubmissionSync } from './httpSubmissionSync'
import { unavailableGrade } from './unavailablePorts'

const GOOGLE_AUTH_ORIGIN = 'https://accounts.google.com/'

async function fetchConnection(): Promise<SchoolConnection> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.classroom.connection)
  return toConnection(ensureOk(response) || {})
}

/** Connection lookups are read-only and identical for every caller on a page;
 *  sharing the in-flight promise keeps the project card from duplicating the
 *  request the connection card already issued. */
let pendingConnection: Promise<SchoolConnection> | null = null

function sharedConnection(): Promise<SchoolConnection> {
  if (!pendingConnection) {
    pendingConnection = fetchConnection().finally(() => {
      pendingConnection = null
    })
  }
  return pendingConnection
}

/** Unauthorized must surface (the card hides itself); anything else that is
 *  not a Classroom error becomes a normalized one. */
function toPortError(error: unknown): ClassroomPortError {
  return error instanceof ClassroomPortError ? error : normalizeError(error)
}

export const httpClassroomAdapters: ClassroomAdapters = {
  connection: {
    getConnection: sharedConnection,

    async startConnect(redirectUri) {
      try {
        const response = await api.post(ARC_MOSAICO_ENDPOINTS.classroom.authorizationUrl, {
          redirectUri,
        })
        const url = String(ensureOk(response)?.url || '')
        // Defensive: only ever hand the browser to Google's consent screen.
        if (!url.startsWith(GOOGLE_AUTH_ORIGIN)) {
          throw new ClassroomPortError('classroom_api_error', 'Unexpected authorization URL')
        }
        window.location.href = url
      } catch (error) {
        throw toPortError(error)
      }
    },

    async completeConnect(code, redirectUri) {
      try {
        const response = await api.post(ARC_MOSAICO_ENDPOINTS.classroom.connection, {
          code,
          redirectUri,
        })
        return toConnection(ensureOk(response) || {})
      } catch (error) {
        throw toPortError(error)
      }
    },

    async disconnect() {
      try {
        ensureOk(await api.delete(ARC_MOSAICO_ENDPOINTS.classroom.connection))
      } catch (error) {
        throw toPortError(error)
      }
    },
  },

  courses: {
    async listCourses() {
      try {
        const response = await api.get(ARC_MOSAICO_ENDPOINTS.classroom.courses)
        const data = ensureOk(response)
        return {
          courses: data.courses || [],
          syncedAt: data.syncedAt ?? null,
          connectionStatus: data.connectionStatus || 'connected',
        }
      } catch (error) {
        throw toPortError(error)
      }
    },

    async listLinkedCourses() {
      try {
        const response = await api.get(ARC_MOSAICO_ENDPOINTS.classroom.linkedCourses)
        return ensureOk(response).courses || []
      } catch (error) {
        throw toPortError(error)
      }
    },
  },

  projectLink: {
    async getLink(projectId) {
      try {
        // Both are fetched in parallel: the caller's own connection gates the
        // link modal of an UNLINKED card, while a linked card takes the status
        // of the connection backing the link from the course payload (see
        // toLinkState). The connection is per user now, so every tutor gets a
        // real 200 here — no more optimistic 401 fallback.
        const [connection, response] = await Promise.all([
          sharedConnection().catch(() => toConnection({})),
          api.get(ARC_MOSAICO_ENDPOINTS.classroom.projectCourse(projectId)),
        ])
        return toLinkState(ensureOk(response) || { linked: false }, connection)
      } catch (error) {
        throw toPortError(error)
      }
    },

    async linkCourse(projectId, courseId) {
      try {
        const response = await api.post(ARC_MOSAICO_ENDPOINTS.classroom.projectCourseLink(projectId), {
          courseId,
        })
        return toLinkResult(ensureOk(response))
      } catch (error) {
        throw toPortError(error)
      }
    },

    async syncRoster(projectId) {
      try {
        const response = await api.post(
          ARC_MOSAICO_ENDPOINTS.classroom.projectCourseSyncRoster(projectId),
        )
        return toLinkResult(ensureOk(response))
      } catch (error) {
        throw toPortError(error)
      }
    },

    async setSyncEnabled(projectId, enabled) {
      try {
        // The caller refreshes right after, so the connection status is filled
        // from what we already know instead of a second round-trip.
        const response = await api.patch(
          ARC_MOSAICO_ENDPOINTS.classroom.projectCourseToggleSync(projectId),
          { enabled },
        )
        const card = { linked: true, course: ensureOk(response).course }
        return toLinkState(card, { connected: true, status: 'connected' })
      } catch (error) {
        throw toPortError(error)
      }
    },

    async unlink(projectId) {
      try {
        ensureOk(await api.delete(ARC_MOSAICO_ENDPOINTS.classroom.projectCourse(projectId)))
      } catch (error) {
        throw toPortError(error)
      }
    },
  },

  submissionSync: httpSubmissionSync,
  grade: unavailableGrade,
}
